
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Bot, ChevronDown, Wallet, Key, BrainCircuit } from 'lucide-react';
import { Button } from '../../ui/Button';

interface ChatSettingsModalProps {
    onClose: () => void;
    // @google/genai-fix: Removed initialApiKey and apiKey from save payload.
    onSave: (settings: { model: string; personality: string; enableHistory: boolean; }) => void;
    initialModel: string;
    initialPersonality: string;
    initialEnableHistory: boolean;
}

export const ChatSettingsModal: React.FC<ChatSettingsModalProps> = ({ onClose, onSave, initialModel, initialPersonality, initialEnableHistory }) => {
    const [provider, setProvider] = useState('google');
    const [model, setModel] = useState(initialModel);
    const [personality, setPersonality] = useState(initialPersonality);
    const [enableHistory, setEnableHistory] = useState(initialEnableHistory);

    const modelsByProvider: Record<string, string[]> = {
        google: ['gemini-3-pro-preview', 'gemini-3-flash-preview', 'gemini-2.5-flash-native-audio-preview-12-2025'],
        openai: ['gpt-4o', 'gpt-4-turbo', 'gpt-3.5-turbo'],
    };

    const handleSave = () => {
        // @google/genai-fix: Strictly passing settings excluding the API key.
        onSave({ model, personality, enableHistory });
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-white dark:bg-[#15171C] rounded-2xl shadow-2xl w-full max-w-lg relative border border-stone-200 dark:border-[#2D3142]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-stone-100 dark:border-white/5 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <Bot size={20} className="text-nobel-gold" />
                        <h2 className="font-serif text-xl text-stone-900 dark:text-white">Chat & AI Agent Settings</h2>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {/* AI Provider & Model Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider">AI Provider</label>
                            <div className="relative mt-1">
                                <select 
                                    value={provider}
                                    onChange={(e) => setProvider(e.target.value)}
                                    className="w-full appearance-none p-3 bg-stone-50 dark:bg-white/5 rounded-lg border border-stone-200 dark:border-white/10 focus:border-nobel-gold outline-none"
                                >
                                    <option value="google">Google AI</option>
                                    <option value="openai">OpenAI</option>
                                </select>
                                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider">Model</label>
                             <div className="relative mt-1">
                                <select 
                                    value={model}
                                    onChange={(e) => setModel(e.target.value)}
                                    className="w-full appearance-none p-3 bg-stone-50 dark:bg-white/5 rounded-lg border border-stone-200 dark:border-white/10 focus:border-nobel-gold outline-none"
                                >
                                    {modelsByProvider[provider].map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    {/* AI Agent Personality */}
                    <div>
                        <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider">Agent Personality</label>
                        <textarea
                            value={personality}
                            onChange={(e) => setPersonality(e.target.value)}
                            className="w-full mt-1 p-3 bg-stone-50 dark:bg-white/5 text-sm text-[#0A0B0F] dark:text-white rounded-lg border border-stone-200 dark:border-white/10 focus:border-nobel-gold outline-none resize-none h-24"
                            placeholder="e.g., You are a helpful assistant for the GhostPay ecosystem..."
                        />
                    </div>
                    
                    {/* Advanced Settings */}
                     <div>
                        <div className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider mb-2">Advanced</div>
                        <div className="space-y-4 p-4 rounded-xl bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10">
                            {/* @google/genai-fix: Removed API Key input section as keys must be exclusively obtained from the environment. */}
                             <div className="flex items-center justify-between pt-1">
                                <label htmlFor="history-toggle" className="flex items-center gap-2 font-medium text-sm text-[#0A0B0F] dark:text-white cursor-pointer"><BrainCircuit size={14}/> Enable Chat Memory</label>
                                <button
                                    id="history-toggle"
                                    onClick={() => setEnableHistory(!enableHistory)}
                                    className={`w-12 h-6 rounded-full p-1 transition-colors ${enableHistory ? 'bg-green-500' : 'bg-stone-300 dark:bg-stone-700'}`}
                                >
                                    <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${enableHistory ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                </button>
                            </div>
                        </div>
                     </div>
                </div>

                <div className="p-6 bg-stone-50 dark:bg-[#0A0B0F] border-t border-stone-100 dark:border-white/5 flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave}>Save Changes</Button>
                </div>
            </motion.div>
        </motion.div>
    );
};
